import { useCallback, useEffect, useRef, useState } from 'react';
import type { FilaJukebox, FutebolPartidaAtiva } from '@/shared/types';
import type { PlayerConfig } from './usePlayerConfig';
import { avaliarJanelaHype } from '../../domain/usecases/hypeMoment';

const TICK_MS = 60_000;

export interface UseHypeMomentParams {
  habilitado:             boolean;
  config:                 Pick<
    PlayerConfig,
    | 'hype_automatico'
    | 'hype_intervalo_min'
    | 'hype_duracao_seg'
    | 'hype_hora_inicio'
    | 'hype_hora_fim'
    | 'hype_texto'
  >;
  pedidoAtual:            FilaJukebox | null;
  showPedidoPopup:        boolean;
  flashAtivo:             boolean;
  partidaAtiva:           FutebolPartidaAtiva | null;
  ultimoFimPedidoCliente: number | null;
}

export interface UseHypeMomentReturn {
  hypeQrDestaque: boolean;
  hypeTexto:      string;
  dispararHype:   (opts?: DispararHypeOptions) => void;
}

export interface DispararHypeOptions {
  /** Ignora janela segura (evento especial do staff) */
  force?:  boolean;
  /** Disparo via controle remoto — não exige hype_automatico */
  manual?: boolean;
}

export function useHypeMoment({
  habilitado,
  config,
  pedidoAtual,
  showPedidoPopup,
  flashAtivo,
  partidaAtiva,
  ultimoFimPedidoCliente,
}: UseHypeMomentParams): UseHypeMomentReturn {
  const [hypeQrDestaque, setHypeQrDestaque] = useState(false);

  const hypePendenteRef = useRef(false);
  const hypeAtivoRef    = useRef(false);
  const lastDisparoRef  = useRef(Date.now());
  const hypeTimeoutRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const configRef       = useRef(config);
  const habilitadoRef   = useRef(habilitado);

  const ctxRef = useRef({
    pedidoAtual,
    showPedidoPopup,
    flashAtivo,
    partidaAtiva,
    ultimoFimPedidoCliente,
  });
  useEffect(() => {
    ctxRef.current = {
      pedidoAtual,
      showPedidoPopup,
      flashAtivo,
      partidaAtiva,
      ultimoFimPedidoCliente,
    };
  });

  useEffect(() => { configRef.current = config; });
  useEffect(() => { habilitadoRef.current = habilitado; });

  const finalizarHype = useCallback(() => {
    hypeAtivoRef.current = false;
    setHypeQrDestaque(false);
    if (hypeTimeoutRef.current) {
      clearTimeout(hypeTimeoutRef.current);
      hypeTimeoutRef.current = null;
    }
  }, []);

  const dispararHype = useCallback((opts?: DispararHypeOptions) => {
    const force  = opts?.force ?? false;
    const manual = opts?.manual ?? false;

    if (!habilitadoRef.current) return;
    if (!manual && !configRef.current.hype_automatico) return;

    const cfg = configRef.current;
    const ctx = ctxRef.current;
    const janela = avaliarJanelaHype({
      pedidoAtual:            !!ctx.pedidoAtual,
      showPedidoPopup:        !!ctx.showPedidoPopup,
      flashAtivo:             ctx.flashAtivo,
      hypeAtivo:              hypeAtivoRef.current,
      partida:                ctx.partidaAtiva,
      ultimoFimPedidoCliente: ctx.ultimoFimPedidoCliente,
      horaInicio:             cfg.hype_hora_inicio,
      horaFim:                cfg.hype_hora_fim,
    });

    if (!janela.ok && !force) {
      if (!manual && !hypePendenteRef.current) {
        hypePendenteRef.current = true;
        console.log('[QrDestaque] adiado:', janela.motivo);
      } else if (manual) {
        console.log('[QrDestaque] manual adiado:', janela.motivo);
      }
      return;
    }

    if (hypeTimeoutRef.current) {
      clearTimeout(hypeTimeoutRef.current);
      hypeTimeoutRef.current = null;
    }

    hypePendenteRef.current = false;
    lastDisparoRef.current = Date.now();
    console.log(manual ? '[QrDestaque] disparado (manual)' : '[QrDestaque] disparado');

    hypeAtivoRef.current = true;
    setHypeQrDestaque(true);

    hypeTimeoutRef.current = setTimeout(() => {
      finalizarHype();
    }, cfg.hype_duracao_seg * 1_000);
  }, [finalizarHype]);

  const tentarDisparo = useCallback(() => {
    if (!habilitadoRef.current || !configRef.current.hype_automatico) return;

    const intervaloMs = configRef.current.hype_intervalo_min * 60_000;
    const elapsed     = Date.now() - lastDisparoRef.current;
    const pronto      = elapsed >= intervaloMs;

    if ((hypePendenteRef.current || pronto) && !hypeAtivoRef.current) {
      dispararHype();
    }
  }, [dispararHype]);

  useEffect(() => {
    if (!habilitado || !config.hype_automatico) {
      finalizarHype();
      hypePendenteRef.current = false;
      return;
    }

    lastDisparoRef.current = Date.now();
    const tick = setInterval(tentarDisparo, TICK_MS);
    return () => clearInterval(tick);
  }, [habilitado, config.hype_automatico, config.hype_intervalo_min, tentarDisparo, finalizarHype]);

  useEffect(() => {
    if (!habilitado || !config.hype_automatico || !hypePendenteRef.current) return;

    const ctx = ctxRef.current;
    const janela = avaliarJanelaHype({
      pedidoAtual:            !!ctx.pedidoAtual,
      showPedidoPopup:        !!ctx.showPedidoPopup,
      flashAtivo:             ctx.flashAtivo,
      hypeAtivo:              hypeAtivoRef.current,
      partida:                ctx.partidaAtiva,
      ultimoFimPedidoCliente: ctx.ultimoFimPedidoCliente,
      horaInicio:             config.hype_hora_inicio,
      horaFim:                config.hype_hora_fim,
    });

    if (!janela.ok) return;

    const intervaloMs = config.hype_intervalo_min * 60_000;
    if (Date.now() - lastDisparoRef.current >= intervaloMs) {
      dispararHype();
    }
  }, [
    habilitado,
    config,
    pedidoAtual,
    showPedidoPopup,
    flashAtivo,
    partidaAtiva,
    ultimoFimPedidoCliente,
    dispararHype,
  ]);

  useEffect(() => () => { finalizarHype(); }, [finalizarHype]);

  return {
    hypeQrDestaque,
    hypeTexto: config.hype_texto,
    dispararHype,
  };
}
